import { FlashList, ViewToken } from '@shopify/flash-list';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, TouchableOpacity } from 'react-native';

import ChatMessage from '@components/Chat/ChatMessage.web';
import { ChatSearchText } from '@components/Chat/ChatSearchText';
import { GroupInviteCard } from '@components/Common/GroupInviteCard';
import EmptyStateNoSearch from '@components/EmptyState/EmptyStateNoSearch';
import { Scrollbar } from '@components/Hoverable/Scrollbar';
import { ChatGroupRequestModal } from '@components/Modals/ChatGroupRequestModal.web';
import { EditMessageHighlightModal } from '@components/Modals/EditMessageHighlightModal.web';
import { MessageEmojiModal } from '@components/Modals/MessageEmojiModal.web';
import { NewChatModal } from '@components/Modals/NewChatModal';
import { NotPaidConfirmModal } from '@components/Modals/NotPaidConfirmModal';
import { Box, Text } from '@components/Restyle';
import { ChatBubbleSkeleton } from '@components/shared/skeletons/ChatBubbleSkeleton';
import { SkeletonList } from '@components/shared/skeletons/SkeletonList';
import {
  Document,
  Message,
  Scalars,
  useCreateChatInviteMutation,
  useGetChatQuery,
  useListPendingUserRequestsQuery,
  UserRequestCategory,
  UserRequestable,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import useChatInput from '@hooks/useChatInput';
import useEmoji from '@hooks/useEmoji';
import { useGetDocumentsByClientIdsQuery } from '@hooks/useGetDocumentsByClientIdsQuery';
import useMe from '@hooks/useMe';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { useCloseOnOutsideClick } from '@hooks/useOutsideClick.web';
import { usePreviousValue } from '@hooks/usePreviousValue';
import {
  compareByCreatedAtThenIdDesc,
  extractName,
  messageDisplayTime,
  propagateReadReceipts,
} from '@utils/chatMessageHelper';

type ChatMessageListProps = {
  list: Message[];
  filterVal?: string;
  fetchAfterCursor: () => void;
  loading: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  onPress?: (message: Message) => void;
  onLongPress?: (message: Message) => void;
  onEditTagPress: () => void;
  onForwardPress: () => void;
  onDeletedPress: () => void;
  onReadByPress: () => void;
  messageIdNeedScrollTo: string | undefined;
  // TA-11079: while a jump is settling, re-aim on every content-size change
  // (rows measuring taller than FlashList's estimate push the target away)
  // instead of waiting for the jump's fixed settle timer. Stops once the user
  // scrolls (scrollNeeded).
  onJumpContentSizeChange?: () => void;
  highlightedToGrey01MessageId?: string;
  onViewableMsgItemsChanged?: (viewItem: ViewToken[]) => void;
  showNewMessageIndicator: (newMsgId: string) => void;
  firstNewMsgId?: string;
  isArchived?: boolean;
  hasNextPage?: boolean;
  isPaginationResolved?: boolean;
  coachmarkEnabled?: boolean;
  onCoachmarkSeen?: () => void;
};
export type ChatMessageListItem = Message | string;

type CompareItem = {
  id: string;
  clientId: string;
  hideSenderInfo: boolean;
  showOnlyTime: boolean;
};

// NOTE: FlashList strongly recommends setting the estimatedItemSize prop and
// recommends a value of 71 for the message list, for items that are purely text.
const ESTIMATED_ITEM_SIZE = 71;
// Consecutive messages from the same sender within this window share one
// header (TA-10892: 5 minutes).
const GROUPING_MESSAGE_TIME = 5 * 60 * 1000;
const REMOVED_FROM_GROUP_PATTERN =
  /^(.*?) has been removed from the group by (.*)$/i;
const MEMBER_ADDED_PATTERN = /^(.*?) has added (.*)$/i;

const ChatMessageList = forwardRef<
  FlashList<ChatMessageListItem>,
  ChatMessageListProps
>(
  (
    {
      list,
      filterVal = '',
      fetchAfterCursor,
      loading,
      onPress,
      onLongPress,
      onEditTagPress,
      onForwardPress,
      onDeletedPress,
      onReadByPress,
      messageIdNeedScrollTo,
      onJumpContentSizeChange,
      highlightedToGrey01MessageId = '',
      onViewableMsgItemsChanged,
      showNewMessageIndicator,
      firstNewMsgId = '',
      isArchived = false,
      hasNextPage = true,
      isPaginationResolved = true,
      coachmarkEnabled,
      onCoachmarkSeen,
    }: ChatMessageListProps,
    ref: React.ForwardedRef<FlashList<ChatMessageListItem>>
  ): JSX.Element => {
    const { t } = useTranslation('format');

    const { isPaid, setPaid, me } = useMe();
    const [isPaidUser, setPaidUser] = useState<boolean>(!!isPaid);
    const [showAddNewMembersModal, setShowAddNewMembersModal] = useState(false);
    const [isLinkCopied, setIsLinkCopied] = useState(false);

    const {
      isScrolling,
      displayMessageList,
      setDisplayMessageList,
      activeChat,
      setIsReadByModalOpen,
    } = useActiveChat();
    const { id: activeChatId } = activeChat || {};

    // TA-10279: `filterVal` (bound to the chat-history-search input) is the
    // same signal the pre-existing `EmptyStateNoSearch` check below already
    // uses to distinguish "actively filtering by a search term" from normal
    // thread browsing — reused here rather than introducing a new heuristic.
    const isSearchMode = !!filterVal;

    // First-open thread skeleton: only the true first fetch of this chat's
    // history (before any message has ever rendered) — never the top-pinned
    // spinner used for older-page pagination once messages already exist.
    // `useListMessagesQuery` is `cache-and-network`, which reports
    // `loading: true` even on a warm-cache mount, so the length check is
    // what keeps this scoped to a genuinely empty thread. Gated on the
    // `list` prop (not `displayMessageList`, which is `useActiveChat()`
    // context state synced by a separate effect and can briefly still hold
    // the PREVIOUS chat's messages on a chat-to-chat switch) so this reads
    // the current chat's data immediately, every render.
    const showThreadSkeleton = useMinimumVisible(
      !isSearchMode && loading && (list?.length ?? 0) === 0
    );
    // Chat-history-search skeleton: `isSearchResultLoading` (computed in
    // ChatDetail) already folds in both the debounce window and the network
    // round-trip. ChatDetail feeds an empty `list` while a FRESH term's
    // fetch is in flight (not a load-more), so `list` is genuinely empty
    // here and this can't fight the previous term's stale bubbles staying
    // on screen.
    const showSearchSkeleton = useMinimumVisible(
      isSearchMode && loading && (list?.length ?? 0) === 0
    );

    const [createChatInvite] = useCreateChatInviteMutation();

    const handleCopyInviteLink = useCallback(async () => {
      if (!activeChatId) return;
      try {
        const result = await createChatInvite({
          variables: { chatId: activeChatId },
        });
        const link = result.data?.createInvite;
        if (!link) return;
        await navigator.clipboard.writeText(link);
        setIsLinkCopied(true);
        setTimeout(() => setIsLinkCopied(false), 3000);
      } catch {
        // silently fail
      }
    }, [activeChatId, createChatInvite]);
    const [showNotPaidConfirmModal, setShowNotPaidConfirmModal] =
      useState<boolean>(false);
    const [compareMessageList, setCompareMessageList] =
      useState<CompareItem[]>();

    const { selectedMessage, setSelectedMessage } = useChatInput();
    const [xPos, setXPos] = useState(0);
    const [yPos, setYPos] = useState(0);
    const [showMenu, setShowMenu] = useState(false);
    const fadeAnim = useState(new Animated.Value(0))[0];
    const mainRef = useRef<HTMLElement>(null);
    const [bodyWidth, setBodyWidth] = useState(0);
    const [bodyHeight, setBodyHeight] = useState(0);
    const {
      setEmojiValue,
      showMessageEmojiModal,
      setShowMessageEmojiModal,
      emojiPositionY,
    } = useEmoji();
    const [scrollPostionY, setScrollPostionY] = useState(0);
    const [showRequestModal, setShowRequestModal] = useState(false);

    const [highlightedToGrey01MessageId1, setHighlightedToGrey01MessageId1] =
      useState('');
    const handleClick = () => {
      setTimeout(() => {
        showMessageEmojiModal && setShowMessageEmojiModal(false);
        setShowMenu(false);
      }, 100);
    };

    const handleContextMenu = (e: any) => {
      e.preventDefault();
    };

    useEffect(() => {
      setHighlightedToGrey01MessageId1(highlightedToGrey01MessageId);
    }, [highlightedToGrey01MessageId]);

    useEffect(() => {
      showMessageEmojiModal && setShowMessageEmojiModal(false);
      const element = mainRef.current;
      if (element) {
        element.addEventListener('click', handleClick);
        element.addEventListener('contextmenu', handleContextMenu);
        return () => {
          element.removeEventListener('click', handleClick);
          element.removeEventListener('contextmenu', handleContextMenu);
        };
      }
    }, []);

    const menuRef = useCloseOnOutsideClick<HTMLDivElement>(
      showMenu,
      () => {
        setTimeout(() => {
          setShowMenu(false);
        }, 100);
      },
      { eventType: 'mousedown' }
    );

    useEffect(() => {
      const animation = Animated.timing(fadeAnim, {
        toValue: showMenu ? 1 : 0,
        duration: 100,
        useNativeDriver: false,
      });
      animation.start();
      // Without this, the animation keeps scheduling requestAnimationFrame
      // ticks against `fadeAnim` even after this component unmounts —
      // in Jest (where rAF is a setTimeout polyfill) those ticks fire after
      // the test's environment has torn down (TA-10378).
      return () => animation.stop();
    }, [showMenu, fadeAnim]);

    const showNotPaidModal = (file: Document) => {
      const createdAt = dayjs(file.createdAt);
      const expired = createdAt?.diff(new Date(), 'day');
      if (!isPaidUser && expired < -1000) {
        setShowNotPaidConfirmModal(true);
        return true;
      }
      return false;
    };

    const [scrollNeeded, setScrollNeeded] = useState(!!messageIdNeedScrollTo);

    // TA-11079: `displayMessageList` (context state) still holds the previous
    // list on first mount and syncs one render later, so FlashList's
    // initialScrollIndex (an index into `list`) would be applied to the wrong
    // data. When mounting with a scroll target, hold the FlashList back until
    // that sync has run — at most one blank frame.
    const [isDisplayListReady, setIsDisplayListReady] = useState(
      !messageIdNeedScrollTo
    );
    useEffect(() => {
      setIsDisplayListReady(true);
    }, []);

    const derivedChatId = activeChat?.id ?? '';
    const { data: chatData } = useGetChatQuery({
      fetchPolicy: 'cache-first',
      variables: { id: derivedChatId },
      skip: !derivedChatId,
    });
    const { getChat: chat } = chatData || {};

    const { data: pendingRequestData } = useListPendingUserRequestsQuery({
      variables: {
        attributes: {
          requestType: UserRequestCategory.MemberAddition,
          userRequestableId: chat?.id ?? '',
          userRequestableType: UserRequestable.Chat,
        },
      },
      skip: !chat?.id,
      fetchPolicy: 'cache-and-network',
    });

    const pendingRequest =
      pendingRequestData?.listPendingUserRequests?.filter(
        (u) => u.status === 'pending' && u.canCurrentUserDecide === true
      ) ?? [];

    const { isGroupChat, createdAt } = chat || {};

    const onRightClick = useCallback(
      (message: Message, mouseX: number, mouseY: number) => {
        if (!chatData?.getChat?.friendRequestAccepted) {
          return;
        }
        setShowMenu(false);
        setSelectedMessage(message);
        setIsReadByModalOpen(false);

        if (bodyHeight - mouseY < 240) {
          mouseY = mouseY - (message.authorId == me?.id ? 323 : 275);
        }
        const element = mainRef.current;
        if (element) {
          const { left, top } = element.getBoundingClientRect();
          mouseX = mouseX - left;
          mouseY = mouseY - top;
        }
        if (bodyWidth - mouseX < 270) {
          mouseX = mouseX - 281;
        }
        setXPos(mouseX);
        setYPos(mouseY);
        setTimeout(() => {
          setShowMenu(true);
        }, 100);
      },
      [bodyWidth, bodyHeight, chatData?.getChat?.friendRequestAccepted]
    );

    const messageList = useMemo(() => {
      // Sort and section by the same timestamp the bubble displays
      // (publishedAt, falling back to createdAt) so a notification card whose
      // record was created earlier than it was published can't land under a
      // different day section than the time it shows (TA-10892 #29/#31).
      const sortedByDate = [...list]
        .map((obj) => ({
          ...obj,
          createdAt: messageDisplayTime(obj),
          original: obj,
        }))
        .sort(compareByCreatedAtThenIdDesc);

      const grouped: Record<string, Message[]> = {};
      for (const { original: obj, createdAt: displayedAt } of sortedByDate) {
        const dateAsKey =
          displayedAt && t('timeAgoMessageList', { val: displayedAt });
        (grouped[dateAsKey] ||= []).push(obj);
      }

      const items = Object.entries(grouped).reduce<ChatMessageListItem[]>(
        (acc, item) => {
          acc.push(...item[1], item[0]);
          return acc;
        },
        []
      );

      return propagateReadReceipts(items);
    }, [list]);

    if (!hasNextPage && isGroupChat && !filterVal) {
      const chatCreateStr = t('timeAgoMessageList', {
        val: createdAt,
      });
      if (messageList[messageList.length - 1] === chatCreateStr) {
        messageList.pop();
      }

      messageList.push(chatCreateStr);
    }

    const ref_flashlist = useRef<FlashList<ChatMessageListItem>>(null);
    const pendingScrollRestoreRef = useRef<number | null>(null);

    useImperativeHandle(
      ref,
      () => ref_flashlist.current as FlashList<ChatMessageListItem>
    );

    // The inverted FlashList is scaleY(-1) on web, so native wheel scrolling
    // runs backwards; re-invert it on the list's own scroll node. Bound here,
    // keyed on isDisplayListReady, because with a scroll target the FlashList
    // mounts one render late and a parent effect bound on mount finds no node.
    useEffect(() => {
      if (!isDisplayListReady) return;
      const scrollNode = ref_flashlist.current?.getScrollableNode?.() as
        | HTMLElement
        | undefined;
      if (!scrollNode?.addEventListener) return;
      const onWheel = (e: WheelEvent) => {
        scrollNode.scrollTop -= e.deltaY;
        e.preventDefault();
      };
      scrollNode.addEventListener('wheel', onWheel);
      return () => scrollNode.removeEventListener('wheel', onWheel);
    }, [isDisplayListReady]);

    // Ensure we don't pass a negative number to the scrollview
    const messageIndexByCursor = Math.max(
      messageList.findIndex((item) => {
        if (typeof item === 'string') {
          return false;
        }
        return item?.id === messageIdNeedScrollTo;
      }),
      0
    );

    const previousFirstMessage = usePreviousValue<Message | undefined>(
      messageList[0]
    );
    const previousMessageListCount = usePreviousValue<number | undefined>(
      messageList.length
    );

    const isEqualMessageListAndDisplayMessageList = _.isEqual(
      messageList,
      displayMessageList
    );

    useEffect(() => {
      if (firstNewMsgId == '') {
        setDisplayMessageList(messageList);
      }
    }, [firstNewMsgId]);

    const debouncedSetDisplayMessageList = useCallback(() => {
      setDisplayMessageList(messageList);
    }, [
      isEqualMessageListAndDisplayMessageList,
      messageList?.length,
      list?.length,
    ]);

    useEffect(() => {
      const firstMessage = messageList[0];
      let isShow = false;
      if (
        firstMessage &&
        previousFirstMessage &&
        previousMessageListCount &&
        firstMessage?.id !== previousFirstMessage?.id
      ) {
        // TA-10213: the sender's own message must never raise the "New
        // Message" CTA. The send path already scrolls to the bottom
        // (ChatDetail's scroll-to-bottom-on-send), and forcing a scroll here
        // when the mutation response lands late would reintroduce the
        // TA-9941 idle yank — so an own message only syncs the list below.
        // A string item (date separator sentinel) can never raise the CTA
        // either: the callback needs a real message id.
        const firstRealMessage =
          typeof firstMessage === 'string' ? undefined : firstMessage;
        if (scrollPostionY <= 50) {
          ref_flashlist?.current?.scrollToIndex({
            index: 0,
            animated: false,
          });

          rendorCompareMessageList(true);
        } else if (firstRealMessage && !firstRealMessage.isSender) {
          showNewMessageIndicator &&
            showNewMessageIndicator(firstRealMessage.id);

          isShow = true;
        }
      }

      // Allow display list to grow even when the new-message indicator is
      // active (firstNewMsgId != ''): pagination keeps adding older messages
      // to messageList but the old guard froze displayMessageList entirely
      // (TA-10379).
      const listGrew = messageList.length > displayMessageList.length;
      if ((firstNewMsgId == '' || listGrew) && !isShow) {
        if (messageList.length === displayMessageList.length) {
          debouncedSetDisplayMessageList();
        } else {
          // Save scroll position when older messages are paginated in so the
          // FlashList can restore the user's reading position after the
          // layout shifts. Find the current display head in messageList — it
          // may be at index > 1 when multiple subscription messages arrived
          // during pagination (TA-10379). Only save when the tail of
          // messageList (from the display head onward) is longer than the
          // current display list, which means old pages were genuinely added
          // rather than only new subscription messages at the head.
          const firstDisplayId =
            typeof displayMessageList[0] !== 'string'
              ? (displayMessageList[0] as Message)?.id
              : null;
          const displayHeadIndex = firstDisplayId
            ? messageList.findIndex(
                (item) =>
                  typeof item !== 'string' &&
                  (item as Message)?.id === firstDisplayId
              )
            : -1;
          const olderPageAdded =
            displayHeadIndex >= 0 &&
            messageList.length - displayHeadIndex > displayMessageList.length;
          if (listGrew && olderPageAdded && scrollPostionY > 50) {
            pendingScrollRestoreRef.current = scrollPostionY;
          }
          setDisplayMessageList(messageList);
        }
      }
    }, [
      isEqualMessageListAndDisplayMessageList,
      firstNewMsgId,
      previousFirstMessage,
      previousMessageListCount,
    ]);

    const { checkDocumentURL } = useGetDocumentsByClientIdsQuery();

    useEffect(() => {
      if (messageList.length > 0) {
        rendorCompareMessageList(true);

        const messages = messageList?.filter(
          (m) => typeof m !== 'string'
        ) as Message[];

        const documents: Document[] = messages
          .flatMap((m) => m?.attachments || [])
          .filter((att): att is Document => att?.__typename === 'Document');

        if (documents.length > 0) {
          checkDocumentURL(documents);
        }
      }
    }, [messageList.length]);

    const wheelEvent = () => {
      setScrollNeeded(false);
      setShowMenu(false);
    };

    useEffect(() => {
      window?.document.addEventListener('wheel', wheelEvent);
      return () => {
        window?.document.removeEventListener('wheel', wheelEvent);
      };
    }, [scrollNeeded]);

    useEffect(() => {
      setScrollNeeded(true);
      setShowMenu(false);
      setScrollPostionY(0);
      pendingScrollRestoreRef.current = null;
    }, [activeChatId]);

    const scrollToMessage = (destinationIndex: number, animate?: boolean) => {
      ref_flashlist?.current?.scrollToIndex({
        index: destinationIndex,
        animated: animate || false,
      });
    };

    const footerComponent = () => {
      return !loading &&
        isPaginationResolved &&
        !hasNextPage &&
        !chat?.settings?.archivedAt &&
        isGroupChat &&
        !chat?.leaveOn ? (
        <Box onMouseDown={(e) => e.preventDefault()}>
          <GroupInviteCard
            disabledButtons={!!chat?.settings?.archivedAt}
            t={t}
            onPressAddMember={() => {
              setShowAddNewMembersModal(true);
            }}
            onPressCopyInviteLine={handleCopyInviteLink}
            isLinkCopied={isLinkCopied}
            isAdmin={chat?.owner?.id === me?.id}></GroupInviteCard>
        </Box>
      ) : (
        <Box marginBottom='m' />
      );
    };

    const rendorCompareMessageList = (renderYn: boolean) => {
      const newList = [...messageList].reverse();
      const compareList = [];
      let groupAuthorId = '';
      let groupFirstMsgTime: Scalars['ISO8601DateTime'];

      for (let index = 0; index < newList.length; index++) {
        const item = newList[index];
        if (item && typeof item !== 'string') {
          let hideSenderInfo = false;
          let showOnlyTime = false;
          if (
            groupFirstMsgTime !== undefined &&
            item.authorId === groupAuthorId
          ) {
            hideSenderInfo = true;
            if (
              new Date(
                item.publishedAt ? item.publishedAt : item.createdAt
              ).getTime() -
                new Date(groupFirstMsgTime).getTime() >
              GROUPING_MESSAGE_TIME
            ) {
              groupFirstMsgTime = item.publishedAt
                ? item.publishedAt
                : item.createdAt;
              showOnlyTime = true;
            }
          } else {
            groupAuthorId = item.authorId;
            groupFirstMsgTime = item.publishedAt
              ? item.publishedAt
              : item.createdAt;
          }

          compareList.push({
            id: item.id,
            clientId: item.clientId,
            hideSenderInfo: hideSenderInfo,
            showOnlyTime: showOnlyTime,
          });
        } else {
          groupFirstMsgTime = undefined;
        }
      }
      if (renderYn) {
        setCompareMessageList(compareList);
      }

      return compareList;
    };

    const renderFlashList = (
      showsVerticalScrollIndicator: boolean,
      setContentOffset?: (offset: { x: number; y: number }) => void,
      setContentSize?: (size: number) => void
    ) => {
      return (
        <FlashList
          onContentSizeChange={(_, height) => {
            setContentSize && setContentSize(height);
            if (pendingScrollRestoreRef.current !== null) {
              ref_flashlist?.current?.scrollToOffset({
                offset: pendingScrollRestoreRef.current,
                animated: false,
              });
              pendingScrollRestoreRef.current = null;
            } else if (scrollNeeded) {
              if (onJumpContentSizeChange) {
                onJumpContentSizeChange();
              } else {
                !isScrolling && scrollToMessage(messageIndexByCursor);
              }
            }
          }}
          onScroll={({ nativeEvent: { contentOffset } }) => {
            setContentOffset && setContentOffset(contentOffset);
            setScrollPostionY(contentOffset.y);
          }}
          onScrollBeginDrag={() => setScrollNeeded(false)}
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={showsVerticalScrollIndicator}
          keyboardShouldPersistTaps='always'
          initialScrollIndex={messageIndexByCursor}
          onScrollToIndexFailed={(info) => {
            if (isScrolling) return;

            const wait = new Promise((resolve) => setTimeout(resolve, 500));

            wait.then(() => {
              scrollToMessage(info.index);
            });
          }}
          ref={ref_flashlist}
          inverted
          data={displayMessageList}
          keyExtractor={(item, index) =>
            typeof item === 'string'
              ? item.toString()
              : (item?.id ?? item?.clientId ?? String(index))
          }
          extraData={[
            displayMessageList?.filter((m) => (m as Message)?.failedAt !== null)
              .length,
            messageList,
          ]}
          onEndReachedThreshold={0.5}
          onEndReached={() => {
            fetchAfterCursor();
          }}
          getItemType={(item: ChatMessageListItem) => {
            // To achieve better performance, specify the type based on the item
            return typeof item === 'string' ? 'sectionHeader' : 'row';
          }}
          estimatedItemSize={ESTIMATED_ITEM_SIZE}
          ListHeaderComponent={() => <Box marginTop='m' />}
          ListFooterComponent={footerComponent}
          viewabilityConfig={{
            itemVisiblePercentThreshold: 80,
          }}
          onViewableItemsChanged={({ viewableItems }) => {
            onViewableMsgItemsChanged &&
              onViewableMsgItemsChanged(viewableItems);
          }}
          renderItem={({ item, index }) => {
            const renderSystemCapsule = (capsuleLabel: string) => (
              <Box alignItems='center' mt='xs'>
                <Box
                  borderWidth={1}
                  borderColor='grey02'
                  backgroundColor='white'
                  borderRadius='xl'
                  px='m'
                  py='xs'>
                  <Text
                    variant='metadata'
                    color='textSecondary'
                    fontWeight='400'>
                    {capsuleLabel}
                  </Text>
                </Box>
              </Box>
            );

            if (typeof item === 'string') {
              return (
                <Box
                  alignItems='center'
                  marginBottom='s'
                  marginTop='s'
                  flexDirection='row'>
                  <Box
                    borderBottomColor='grey02'
                    borderBottomWidth={1}
                    flex={1}
                    ml='m'></Box>
                  <Text
                    variant='metadata'
                    mx='m'
                    color='grey03'
                    selectable
                    style={{ cursor: 'text' }}>
                    {item}
                  </Text>
                  <Box
                    borderBottomColor='grey02'
                    borderBottomWidth={1}
                    flex={1}
                    mr='m'></Box>
                </Box>
              );
            }
            if (
              item.authorEmail === process.env.SYSTEM_NOTIFICATION_EMAIL &&
              !item.userRequest
            ) {
              if (!item.body) {
                return null;
              }
              const bodyText = item.body.trim();
              const removedFromGroupMatch = bodyText.match(
                REMOVED_FROM_GROUP_PATTERN
              );
              if (removedFromGroupMatch) {
                const removedMemberName = (
                  removedFromGroupMatch[1] ?? ''
                ).trim();
                const removedByName = (removedFromGroupMatch[2] ?? '').trim();
                const isGroupAdmin = chat?.owner?.id === me?.id;
                const capsuleLabel = isGroupAdmin
                  ? `You removed ${removedMemberName}`
                  : `${removedMemberName} was removed by ${removedByName}`;

                return renderSystemCapsule(capsuleLabel);
              }

              const memberAddedMatch = bodyText.match(MEMBER_ADDED_PATTERN);
              if (memberAddedMatch) {
                const addedByName = (memberAddedMatch[1] ?? '').trim();
                const addedMemberName = (memberAddedMatch[2] ?? '').trim();
                return renderSystemCapsule(
                  `${addedByName} has added ${addedMemberName}`
                );
              }
              return (
                <Box alignItems='center' mt='xs'>
                  <ChatSearchText
                    textToHighlight={item.body!}
                    searchWords={extractName(item.body!)}
                  />
                </Box>
              );
            }

            const comparisonData = compareMessageList?.find(
              (c) => c.id === item.id || c.clientId === item.clientId
            );
            let hideSenderInfo = comparisonData?.hideSenderInfo || false;
            let showOnlyTime = comparisonData?.showOnlyTime || false;
            if (index == 0 && item.isDraft && comparisonData == undefined) {
              const list = rendorCompareMessageList(false);
              const comparisonData = list?.find(
                (c) => c.id === item.id || c.clientId === item.clientId
              );
              hideSenderInfo = comparisonData?.hideSenderInfo || false;
              showOnlyTime = comparisonData?.showOnlyTime || false;
            }

            return (
              <Box marginTop={hideSenderInfo ? undefined : 'xs'}>
                <ChatMessage
                  message={item}
                  chatData={chatData}
                  filterVal={filterVal}
                  onPress={() => onPress && onPress(item)}
                  onLongPress={() => onLongPress && onLongPress(item)}
                  onEditTagPress={() => {
                    onEditTagPress();
                  }}
                  onForwardPress={() => onForwardPress()}
                  highlighted={item.id === messageIdNeedScrollTo}
                  highlightedToGrey01={
                    item.id === highlightedToGrey01MessageId1
                  }
                  showNotPaidModal={(file: Document) =>
                    showNotPaidModal?.(file)
                  }
                  hideSenderInfo={hideSenderInfo}
                  showOnlyTime={showOnlyTime}
                  onRightClick={onRightClick}
                  coachmarkEnabled={coachmarkEnabled}
                  onCoachmarkSeen={onCoachmarkSeen}
                />
              </Box>
            );
          }}
        />
      );
    };

    return (
      <Box
        flex={1}
        ref={mainRef}
        onLayout={(e: any) => {
          if (e.nativeEvent.layout.width > 0) {
            setBodyWidth(e.nativeEvent.layout.width);
            setBodyHeight(e.nativeEvent.layout.height);
          }
        }}>
        {/*Pls do not delete here , waiting for BE, the data is for UI, need to replace with real data*/}
        {chat?.owner?.id === me?.id &&
          (chat?.isGroupChat ?? false) &&
          pendingRequest.length > 0 && (
            <Box height={32} backgroundColor='grey05' py='xs' px='m'>
              <TouchableOpacity
                onPress={() => {
                  setShowRequestModal(true);
                }}>
                <Text
                  variant='webBodySecondary'
                  textDecorationLine='underline'
                  color='white'>
                  {t('models:chat.youHaveRequests', {
                    requestCount: pendingRequest.length,
                  })}
                </Text>
              </TouchableOpacity>
            </Box>
          )}

        <Scrollbar
          inverted
          HoverableProps={{
            style: { flex: 1, cursor: 'default' },
          }}>
          {({ setContentOffset, setContentSize, scrollTo }) => {
            scrollTo !== undefined &&
              ref_flashlist.current?.scrollToOffset({ offset: scrollTo });
            const showSkeleton = showThreadSkeleton || showSearchSkeleton;
            return (
              <>
                {(list?.length ?? 0) === 0 &&
                  filterVal?.length > 0 &&
                  !loading &&
                  !showSkeleton && <EmptyStateNoSearch />}
                {isDisplayListReady &&
                  renderFlashList(false, setContentOffset, setContentSize)}
                {/* TA-10617: no bare-`loading` spinner here. `useListMessagesQuery`
                    is cache-and-network, so `loading` also flips true on a warm
                    reopen of an already-cached chat (messages already rendering) —
                    it was never a reliable "fetchAfterCursor pagination in flight"
                    signal (notifyOnNetworkStatusChange isn't set, so fetchMore
                    doesn't surface through this `loading` at all). The skeleton
                    above already owns first-open loading; nothing here should. */}
                {showSkeleton && (
                  // TA-10378: QA confirmed that swapping the real FlashList
                  // out for the skeleton (the pre-existing ternary here) —
                  // then back once loading settles — reversed the inverted
                  // list's mouse-wheel scroll direction. Overlaying the
                  // skeleton on top instead keeps FlashList mounted
                  // throughout. `pointerEvents='none'` lets wheel events
                  // fall through to the FlashList underneath, and the
                  // opaque `grey01` background (the thread's own
                  // background, per ChatBubbleSkeleton) covers it while
                  // `displayMessageList` briefly lags behind `list` (see
                  // the cross-chat staleness note above).
                  <Box
                    position='absolute'
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    pointerEvents='none'
                    justifyContent='flex-end'
                    backgroundColor='grey01'>
                    <SkeletonList
                      count={3}
                      row={() => <ChatBubbleSkeleton />}
                    />
                  </Box>
                )}
              </>
            );
          }}
        </Scrollbar>
        {showMenu && (
          <Animated.View
            ref={menuRef}
            style={{
              width: 281,
              position: 'absolute',
              opacity: fadeAnim,
              top: yPos,
              left: xPos,
            }}>
            <EditMessageHighlightModal
              onDismiss={() => setShowMenu(false)}
              onEditTagPress={onEditTagPress}
              onForwardPress={onForwardPress}
              onDeletePress={onDeletedPress}
              onEmoji={setEmojiValue}
              isArchived={isArchived}
              onReadByPress={onReadByPress}
            />
          </Animated.View>
        )}
        {showMessageEmojiModal && selectedMessage && emojiPositionY > 0 && (
          <Box
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              bottom: 0,
              left: 0,
              zIndex: 1000,
            }}>
            <MessageEmojiModal
              bodyHeight={bodyHeight}
              reaction={selectedMessage.reaction}
            />
          </Box>
        )}
        {showAddNewMembersModal && (
          <NewChatModal
            chatId={derivedChatId}
            showModal={showAddNewMembersModal}
            onClose={() => {
              setShowAddNewMembersModal(false);
            }}
          />
        )}
        {showNotPaidConfirmModal && (
          <NotPaidConfirmModal
            showModal={showNotPaidConfirmModal}
            onClose={() => setShowNotPaidConfirmModal(false)}
            onPress={() => {
              setShowNotPaidConfirmModal(false);
              setPaid(true);
              setPaidUser(true);
            }}
            title='Chat'
          />
        )}
        {showRequestModal && chat?.id && (
          <ChatGroupRequestModal
            chatId={chat.id}
            visible={showRequestModal}
            onClose={() => {
              setShowRequestModal(false);
            }}
          />
        )}
      </Box>
    );
  }
);

export default ChatMessageList;
